/*
 * Named points of a widget (`points`: name -> binding) as live values and as
 * the context the script language reads from.
 *
 * Reading `.value` of the point signals inside render subscribes the
 * component (Preact signals), so a rule or template re-renders when one of
 * its points changes, and only then.
 */

import { useEffect } from 'preact/hooks';
import type { IPointBindingSource, WriteAcknowledgement } from '../binding';
import { pointSignal } from '../pointSignal';
import type { ScriptContext } from '../script/hmiScript';

export interface NamedPoints {
  /** Current raw values by name (undefined until the first value). */
  values: Record<string, unknown>;
  ctx: ScriptContext;
  /** Binding key of a name. */
  binding(name: string): string | undefined;
}

export function useNamedPoints(points: Record<string, string> | undefined, source: IPointBindingSource | null): NamedPoints {
  const entries = Object.entries(points ?? {});
  const subs = source ? entries.map(([name, key]) => ({ name, key, sub: pointSignal(source, key) })) : [];
  // A fresh subscription set per render, released by the next render's
  // cleanup; pointSignal shares the underlying stream, so this is cheap.
  useEffect(() => () => subs.forEach((s) => s.sub.release()), [subs]);

  const values: Record<string, unknown> = {};
  for (const s of subs) values[s.name] = s.sub.value.value?.value ?? undefined;
  const names = new Set(entries.map(([n]) => n));
  return {
    values,
    ctx: { pointNames: names, value: (n) => values[n] },
    binding: (n) => points?.[n],
  };
}

/** Drain a write to its end. True when confirmed. */
export async function drainWrite(
  source: IPointBindingSource,
  pointId: string,
  value: unknown,
  signal: AbortSignal,
): Promise<boolean> {
  let terminal: WriteAcknowledgement | null = null;
  for await (const ack of source.write(pointId, value, signal)) {
    if (ack.state === 'confirmed' || ack.state === 'rejected') {
      terminal = ack;
      break;
    }
  }
  return terminal?.state === 'confirmed';
}
